import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Calendar as CalendarIcon, Car } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import Seo from "@/components/Seo";
import { Calendar } from "@/components/ui/calendar";
import AvailabilityLegend from "@/components/AvailabilityLegend";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DEFAULT_SERVICE_OPTIONS = [
  { value: "without_driver", labelIt: "Senza autista", labelEn: "Without driver" },
  { value: "with_driver", labelIt: "Con autista", labelEn: "With driver" },
  {
    value: "with_driver_helper",
    labelIt: "Con autista + necroforo",
    labelEn: "With driver + pallbearer",
  },
];

const normaliseServiceTypes = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_SERVICE_OPTIONS;
  return raw
    .filter((s) => s && s.value)
    .map((s) => ({
      value: s.value,
      labelIt: s.label_it || s.labelIt || s.value,
      labelEn: s.label_en || s.labelEn || s.label_it || s.labelIt || s.value,
    }));
};

const toISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function Booking() {
  const { lang, t } = useLang();
  const [content, setContent] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleId, setVehicleId] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [serviceType, setServiceType] = useState("with_driver");
  const serviceOptions = useMemo(
    () => normaliseServiceTypes(content?.service_types),
    [content?.service_types],
  );

  // Ensure selected service type is valid for the current options
  useEffect(() => {
    if (!serviceOptions.length) return;
    if (!serviceOptions.some((s) => s.value === serviceType)) {
      setServiceType(serviceOptions[0].value);
    }
  }, [serviceOptions, serviceType]);
  const [form, setForm] = useState({
    customer_name: "",
    customer_company: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Anti-spam: math captcha + honeypot
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, op: "+" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const newCaptcha = () => {
    const a = Math.floor(Math.random() * 8) + 2; // 2..9
    const b = Math.floor(Math.random() * 8) + 1; // 1..8
    const op = Math.random() < 0.5 ? "+" : "-";
    // Ensure a >= b for subtraction so result stays positive
    const [x, y] = op === "-" && b > a ? [b, a] : [a, b];
    setCaptcha({ a: x, b: y, op });
    setCaptchaAnswer("");
  };

  useEffect(() => {
    newCaptcha();
  }, []);

  const captchaExpected = captcha.op === "+" ? captcha.a + captcha.b : captcha.a - captcha.b;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, v] = await Promise.all([api.get("/content"), api.get("/vehicles")]);
        if (!alive) return;
        if (!c.data?.booking_enabled) {
          window.location.replace("/");
          return;
        }
        setContent(c.data);
        setVehicles(v.data);
        if (v.data.length === 1) setVehicleId(v.data[0].id);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const vehicle = useMemo(
    () => vehicles.find((v) => v.id === vehicleId) || null,
    [vehicles, vehicleId]
  );

  const blockedDates = useMemo(() => {
    if (!vehicle) return [];
    return vehicle.blocked_dates.map((s) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
  }, [vehicle]);

  const isBlockedDate = (date) => date < new Date(new Date().setHours(0, 0, 0, 0)) ||
    blockedDates.some((bd) => bd.toDateString() === date.toDateString());

  const onSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: a real user never fills this hidden field
    if (honeypot.trim() !== "") {
      // Pretend success silently to confuse bots
      setDone(true);
      return;
    }
    if (parseInt(captchaAnswer, 10) !== captchaExpected) {
      toast.error(
        lang === "it"
          ? "Risposta al controllo anti-spam errata"
          : "Anti-spam check failed",
      );
      newCaptcha();
      return;
    }
    if (!vehicleId) {
      toast.error(lang === "it" ? "Seleziona un veicolo" : "Select a vehicle");
      return;
    }
    if (!selectedDates.length) {
      toast.error(lang === "it" ? "Seleziona almeno una data" : "Select at least one date");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        vehicle_id: vehicleId,
        dates: selectedDates.map(toISO).sort(),
        service_type: serviceType,
        ...form,
      });
      setDone(true);
      toast.success(
        lang === "it" ? "Richiesta inviata!" : "Request sent!"
      );
    } catch (err) {
      toast.error(formatApiError(err));
      newCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-afb-bg">
        <div className="text-afb-muted text-sm tracking-widest uppercase">
          {t("common.h24") && "Caricamento..."}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-afb-bg min-h-screen" data-testid="booking-page">
      <Seo
        content={content}
        overrides={{
          title:
            lang === "it"
              ? `${content.booking_label_it || "Prenotazione"} — Agenzia Funebre Barone`
              : `${content.booking_label_en || "Booking"} — Barone Funeral Agency`,
        }}
      />
      <Header
        manifestoEnabled={!!content.manifesto_enabled}
        bookingEnabled={!!content.booking_enabled}
        bookingLabel={
          lang === "it" ? content.booking_label_it : content.booking_label_en
        }
      />

      <main className="pt-28 sm:pt-32 pb-20">
        <div className="afb-container">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-afb-muted hover:text-afb-gold mb-8"
            data-testid="booking-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {lang === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="max-w-2xl mb-10 sm:mb-14">
            <span className="afb-eyebrow">
              {lang === "it" ? "Noleggio veicoli" : "Vehicle rental"}
            </span>
            <h1 className="serif text-[1.9rem] sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.15] text-afb-ink">
              {lang === "it"
                ? content.booking_label_it || "Prenotazione"
                : content.booking_label_en || "Booking"}
            </h1>
            <span className="block mt-5 h-[2px] w-16 sm:w-20 bg-[color:var(--afb-gold)]" />
            {(lang === "it" ? content.booking_intro_it : content.booking_intro_en) && (
              <p className="mt-6 text-base text-afb-muted leading-relaxed">
                {lang === "it" ? content.booking_intro_it : content.booking_intro_en}
              </p>
            )}
          </div>

          {done ? (
            <div className="max-w-xl mx-auto text-center py-16 px-6 bg-white border border-[color:var(--afb-cream)] rounded-md">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-5">
                <Check className="w-7 h-7" />
              </div>
              <h2 className="serif text-2xl text-afb-ink">
                {lang === "it" ? "Richiesta ricevuta" : "Request received"}
              </h2>
              <p className="text-sm text-afb-muted mt-3 leading-relaxed">
                {lang === "it"
                  ? "Ti contatteremo a breve all'indirizzo email indicato per confermare la prenotazione."
                  : "We'll contact you shortly at the email you provided to confirm the booking."}
              </p>
              <Link
                to="/"
                className="afb-btn-gold mt-6 inline-flex"
                data-testid="booking-home"
              >
                Home
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* LEFT */}
              <div className="space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-afb-muted flex items-center gap-2">
                    <Car className="w-3.5 h-3.5" />
                    {lang === "it" ? "Seleziona veicolo" : "Select vehicle"}
                  </Label>
                  {vehicles.length === 0 ? (
                    <div className="mt-3 p-5 border border-dashed border-[color:var(--afb-cream)] rounded-md text-sm text-afb-muted text-center">
                      {lang === "it"
                        ? "Nessun veicolo disponibile al momento."
                        : "No vehicles available right now."}
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {vehicles.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => {
                            setVehicleId(v.id);
                            setSelectedDates([]);
                          }}
                          className={`text-left p-4 rounded-md border transition-all ${
                            vehicleId === v.id
                              ? "border-afb-gold bg-afb-cream/40 ring-1 ring-afb-gold"
                              : "border-[color:var(--afb-cream)] bg-white hover:border-afb-gold/60"
                          }`}
                          data-testid={`vehicle-${v.id}`}
                        >
                          <div className="flex gap-3">
                            {v.photo_url ? (
                              <img
                                src={v.photo_url}
                                alt={v.name}
                                className="w-16 h-16 object-cover rounded border border-[color:var(--afb-cream)]"
                              />
                            ) : (
                              <span className="w-16 h-16 rounded bg-afb-cream flex items-center justify-center">
                                <Car className="w-6 h-6 text-afb-gold" />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="serif text-base text-afb-ink truncate">{v.name}</div>
                              {v.model_label && (
                                <div className="text-xs text-afb-muted truncate">{v.model_label}</div>
                              )}
                              {v.targa && (
                                <div className="text-[10px] uppercase tracking-widest text-afb-gold mt-1">
                                  {v.targa}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-afb-muted flex items-center gap-2">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {lang === "it" ? "Seleziona le date" : "Select dates"}
                  </Label>
                  <p className="text-xs text-afb-muted mt-1">
                    {lang === "it"
                      ? "Tocca una o più date. Le date in grigio non sono disponibili."
                      : "Tap one or more dates. Greyed-out dates are not available."}
                  </p>
                  <div className="mt-3 bg-white border border-[color:var(--afb-cream)] rounded-md p-3 inline-block">
                    <Calendar
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(d) => setSelectedDates(d || [])}
                      disabled={isBlockedDate}
                      data-testid="booking-calendar"
                      classNames={{
                        day: "h-9 w-9 p-0 font-semibold rounded-md bg-green-700 text-white hover:bg-green-600 transition-colors",
                        day_selected: "!bg-gray-600 !text-white hover:!bg-gray-700",
                        day_disabled: "!bg-red-700 !text-white !opacity-100 cursor-not-allowed hover:!bg-red-700",
                        day_outside: "!bg-transparent !text-zinc-300 opacity-60",
                        day_today: "!ring-2 !ring-[color:var(--afb-gold)]",
                      }}
                    />
                  </div>
                  <AvailabilityLegend variant="public" />
                  {selectedDates.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {selectedDates
                        .slice()
                        .sort((a, b) => a - b)
                        .map((d) => (
                          <span
                            key={d.toISOString()}
                            className="text-xs px-2 py-1 rounded-full bg-afb-cream text-afb-ink"
                          >
                            {d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div className="space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-afb-muted">
                    {lang === "it" ? "Tipo di servizio" : "Service type"}
                  </Label>
                  <div className="mt-3 space-y-2">
                    {serviceOptions.map((s) => (
                      <label
                        key={s.value}
                        className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer ${
                          serviceType === s.value
                            ? "border-afb-gold bg-afb-cream/40"
                            : "border-[color:var(--afb-cream)] bg-white hover:border-afb-gold/60"
                        }`}
                      >
                        <input
                          type="radio"
                          name="service"
                          value={s.value}
                          checked={serviceType === s.value}
                          onChange={(e) => setServiceType(e.target.value)}
                          className="accent-[color:var(--afb-gold)]"
                          data-testid={`service-${s.value}`}
                        />
                        <span className="text-sm text-afb-ink">
                          {lang === "it" ? s.labelIt : s.labelEn}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FieldInput
                    label={lang === "it" ? "Nome e cognome" : "Full name"}
                    value={form.customer_name}
                    onChange={(v) => setForm((f) => ({ ...f, customer_name: v }))}
                    required
                    testId="booking-name"
                  />
                  <FieldInput
                    label={lang === "it" ? "Agenzia" : "Company"}
                    value={form.customer_company}
                    onChange={(v) => setForm((f) => ({ ...f, customer_company: v }))}
                    testId="booking-company"
                  />
                  <FieldInput
                    label="Email"
                    type="email"
                    value={form.customer_email}
                    onChange={(v) => setForm((f) => ({ ...f, customer_email: v }))}
                    required
                    testId="booking-email"
                  />
                  <FieldInput
                    label={lang === "it" ? "Telefono" : "Phone"}
                    type="tel"
                    value={form.customer_phone}
                    onChange={(v) => setForm((f) => ({ ...f, customer_phone: v }))}
                    testId="booking-phone"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-afb-muted">
                    {lang === "it" ? "Note (opzionale)" : "Notes (optional)"}
                  </Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={4}
                    className="mt-2"
                    data-testid="booking-notes"
                  />
                </div>

                {/* Honeypot — hidden from real users, bots fill it */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                  }}
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </label>
                </div>

                {/* Math captcha */}
                <div className="p-4 bg-afb-cream/40 border border-[color:var(--afb-cream)] rounded-md">
                  <Label className="text-xs uppercase tracking-widest text-afb-muted">
                    {lang === "it" ? "Controllo anti-spam" : "Anti-spam check"}
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <span
                      className="serif text-2xl text-afb-ink select-none"
                      data-testid="captcha-question"
                    >
                      {captcha.a} {captcha.op} {captcha.b} =
                    </span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="-?[0-9]*"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^\d-]/g, ""))}
                      required
                      className="w-24 text-center text-lg"
                      placeholder="?"
                      data-testid="captcha-answer"
                    />
                    <button
                      type="button"
                      onClick={newCaptcha}
                      className="text-xs text-afb-muted hover:text-afb-gold underline"
                      data-testid="captcha-refresh"
                    >
                      {lang === "it" ? "Nuovo numero" : "New number"}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-afb-ink hover:bg-black text-white rounded-full w-full h-12"
                  data-testid="booking-submit"
                >
                  {submitting
                    ? "..."
                    : lang === "it"
                      ? "Invia richiesta"
                      : "Send request"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer content={content} />
      <WhatsAppFab content={content} />
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-afb-muted">
        {label}
        {required && <span className="text-afb-gold ml-0.5">*</span>}
      </Label>
      <Input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2"
        data-testid={testId}
      />
    </div>
  );
}
